import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import config from './config';
import DynamicComponentRenderer from './components/dynamic/DynamicComponentRenderer';
import { CategoryDynamicComponent } from './types/dynamicComponents';
import { CourseSummary } from './courseApi';
import { getOrganizationHeaders, withOrganizationSlug } from './orgContext';

interface Category {
  id: number;
  name: string;
  description: string;
  sortOrder?: number;
  detailedDescription?: string;
  tips?: string;
  example?: string;
  curriculumContentDescriptionCode?: string;
  curriculumContentDescription?: string;
  curriculumAchievementStandard?: string;
  curriculumElaboration?: string;
  curriculumStrand?: string;
  curriculumSubStrand?: string;
  curriculumLearningAreaCapability?: string;
  curriculumDisciplineLanguage?: string;
  curriculumLevelBand?: string;
  curriculumPathway?: string;
  curriculumCrossCurriculumPriority?: string;
  curriculumOrganisingIdea?: string;
  curriculumSourceFileName?: string;
  categoryDiagrams?: CategoryDiagram[];
  dynamicComponents?: CategoryDynamicComponent[];
  subCategories?: Category[];
}

interface CategoryDiagram {
  id: number;
  diagramId: number;
  imageUrl?: string;
  placementType: number;
  displaySize?: number;
  displayOrder: number;
  introHtml?: string;
  captionOverride?: string;
  diagramType: string;
  defaultCaption?: string;
}

const getDiagramImageBaseUrl = (): string => {
  try {
    const apiUrl = new URL(config.apiBaseUrl);
    const normalizedPath = apiUrl.pathname.replace(/\/+$/, '').replace(/\/api$/i, '');
    return `${apiUrl.origin}${normalizedPath ? `${normalizedPath}/` : '/'}`;
  } catch {
    return `${config.apiBaseUrl.replace(/\/+$/, '').replace(/\/api$/i, '')}/`;
  }
};

const DIAGRAM_IMAGE_BASE_URL = getDiagramImageBaseUrl();

const resolveDiagramImageUrl = (imageUrl: string): string => {
  const trimmedUrl = imageUrl.trim();

  if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
    return trimmedUrl;
  }

  const normalizedPath = trimmedUrl.startsWith('/') ? trimmedUrl.substring(1) : trimmedUrl;
  return `${DIAGRAM_IMAGE_BASE_URL}${normalizedPath}`;
};

const getDiagramWidthBySize = (displaySize?: number): string => {
  switch (displaySize) {
    case 1:
      return '60%';
    case 3:
      return '90%';
    case 4:
      return '100%';
    case 2:
    default:
      return '75%';
  }
};

type Course = CourseSummary;

interface UserEnrollment {
  courseId: number;
  subscriptionTier: number;
}

const getCategorySortOrder = (category: Category): number => {
  if (typeof category.sortOrder === 'number' && category.sortOrder > 0) {
    return category.sortOrder;
  }

  return Number.MAX_SAFE_INTEGER;
};

const sortCategoriesByLevelAndOrder = (items: Category[]): Category[] => {
  return [...items]
    .sort((first, second) => {
      const firstOrder = getCategorySortOrder(first);
      const secondOrder = getCategorySortOrder(second);

      if (firstOrder !== secondOrder) {
        return firstOrder - secondOrder;
      }

      return first.name.localeCompare(second.name);
    })
    .map((item) => ({
      ...item,
      subCategories: item.subCategories ? sortCategoriesByLevelAndOrder(item.subCategories) : item.subCategories,
    }));
};

const pickFirstCategory = (items: Category[]): Category | null => {
  for (const item of items) {
    if (item.subCategories && item.subCategories.length > 0) {
      const firstSub = pickFirstCategory(item.subCategories);
      if (firstSub) return firstSub;
    }
    return item;
  }
  return null;
};

const getDisplayCategoryName = (category: Category | null): string => {
  if (!category) {
    return 'Select a Category';
  }

  return (category.curriculumContentDescription?.trim() || category.name).trim();
};

const getTreeCategoryName = (category: Category): string => {
  const subStrand = category.curriculumSubStrand?.trim();
  const contentDescriptionCode = category.curriculumContentDescriptionCode?.trim();
  const contentDescription = category.curriculumContentDescription?.trim();

  if (subStrand && contentDescriptionCode) {
    return `${subStrand}-${contentDescriptionCode}`;
  }

  if (subStrand) {
    return subStrand;
  }

  if (contentDescription) {
    return contentDescription;
  }

  return category.name;
};

const getCurriculumMetadataItems = (category: Category | null): Array<{ label: string; value: string; fullWidth?: boolean }> => {
  if (!category) {
    return [];
  }

  const items: Array<{ label: string; value: string | undefined; fullWidth?: boolean }> = [
    { label: 'Content Description Code', value: category.curriculumContentDescriptionCode },
    { label: 'Strand', value: category.curriculumStrand },
    { label: 'Sub-strand', value: category.curriculumSubStrand },
    { label: 'Learning Area', value: category.curriculumLearningAreaCapability },
    { label: 'Discipline / Language', value: category.curriculumDisciplineLanguage },
    { label: 'Level Band', value: category.curriculumLevelBand },
    { label: 'Pathway', value: category.curriculumPathway },
    { label: 'Cross-curriculum Priority', value: category.curriculumCrossCurriculumPriority },
    { label: 'Organising Idea', value: category.curriculumOrganisingIdea },
    { label: 'Achievement Standard', value: category.curriculumAchievementStandard, fullWidth: true },
    { label: 'Elaboration', value: category.curriculumElaboration, fullWidth: true },
  ];

  return items
    .filter((item) => (item.value ?? '').trim().length > 0)
    .map((item) => ({ label: item.label, value: (item.value ?? '').trim(), fullWidth: item.fullWidth }));
};

const CourseDetails: React.FC = () => {
  const { courseId, organizationSlug } = useParams<{ courseId: string; organizationSlug?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();

  const slugAwarePath = (path: string) =>
    organizationSlug ? `/${organizationSlug}${path}` : withOrganizationSlug(path);

  const categoryIdFromQuery = new URLSearchParams(location.search).get('categoryId');
  const numericCategoryIdFromQuery = categoryIdFromQuery ? parseInt(categoryIdFromQuery, 10) : null;

  const [course, setCourse] = useState<Course | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
  const [categorySearch, setCategorySearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedCategoryDetails, setSelectedCategoryDetails] = useState<Category | null>(null);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enrollment, setEnrollment] = useState<UserEnrollment | null>(null);
  const [showCategoriesMenu, setShowCategoriesMenu] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    // If a specific category is requested via query string, show the detail panel directly
    if (numericCategoryIdFromQuery) {
      return false;
    }
    return window.innerWidth < 1024;
  });
  const [dynamicComponentSelections, setDynamicComponentSelections] = useState<Record<number, number>>({});
  const syllabusToggleButtonRef = useRef<HTMLButtonElement | null>(null);
  const syllabusSectionRef = useRef<HTMLDivElement | null>(null);

  const normalizedCourseParam = (courseId ?? '').trim();
  const parsedCourseId = Number(normalizedCourseParam);
  const numericCourseId = Number.isFinite(parsedCourseId) && parsedCourseId > 0 ? parsedCourseId : null;
  const apiBaseUrl = `${config.apiBaseUrl}/api`;

  useEffect(() => {
    const fetchCourse = async () => {
      if (!normalizedCourseParam) {
        setError('Invalid course id.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        if (numericCourseId) {
          const response = await axios.get<Course>(`${apiBaseUrl}/courses/${numericCourseId}`, {
            headers: {
              ...getOrganizationHeaders(),
            },
          });

          setCourse(response.data ?? null);
          return;
        }

        const response = await axios.get<Course>(`${apiBaseUrl}/courses/by-ref/${encodeURIComponent(normalizedCourseParam)}`, {
          headers: {
            ...getOrganizationHeaders(),
          },
        });

        if (!response.data) {
          setError('Course not found.');
          setCourse(null);
        } else {
          setCourse(response.data);
        }
      } catch (err) {
        console.error('Failed to load course details:', err);
        setError('Failed to load course details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [normalizedCourseParam, numericCourseId, apiBaseUrl]);

  const resolvedCourseId = course?.id ?? numericCourseId;

  useEffect(() => {
    const fetchCategories = async () => {
      if (!resolvedCourseId) {
        setCategories([]);
        setExpandedCategories(new Set());
        setSelectedCategory(null);
        setSelectedCategoryDetails(null);
        setCategoriesLoading(false);
        return;
      }

      const cacheKey = `course-details-categories-v2-${resolvedCourseId}`;
      const shouldAutoSelectCategory = typeof window === 'undefined' ? true : window.innerWidth >= 1024;
      const findCategoryInTree = (items: Category[], categoryId: number): Category | null => {
        for (const item of items) {
          if (item.id === categoryId) {
            return item;
          }

          if (item.subCategories && item.subCategories.length > 0) {
            const found = findCategoryInTree(item.subCategories, categoryId);
            if (found) {
              return found;
            }
          }
        }

        return null;
      };

      try {
        setCategoriesLoading(true);

        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          try {
            const cachedData = JSON.parse(cached) as Category[];
            if (Array.isArray(cachedData) && cachedData.length > 0) {
              const sortedCachedData = sortCategoriesByLevelAndOrder(cachedData);
              setCategories(sortedCachedData);
              setExpandedCategories(new Set());
              setSelectedCategory((prev) => {
          if (prev) {
            return findCategoryInTree(sortedCachedData, prev.id) ?? prev;
          }
          if (numericCategoryIdFromQuery) {
            const fromQuery = findCategoryInTree(sortedCachedData, numericCategoryIdFromQuery);
            if (fromQuery) return fromQuery;
          }
          return shouldAutoSelectCategory ? pickFirstCategory(sortedCachedData) : null;
        });
            }
          } catch {
            sessionStorage.removeItem(cacheKey);
          }
        }

        const response = await axios.get<Category[]>(`${apiBaseUrl}/categories/by-course/${resolvedCourseId}`, {
          headers: {
            ...getOrganizationHeaders(),
          },
        });
        const data = sortCategoriesByLevelAndOrder(response.data ?? []);
        setCategories(data);
        setExpandedCategories(new Set());
        setSelectedCategory((prev) => {
          if (prev) {
            return findCategoryInTree(data, prev.id) ?? (shouldAutoSelectCategory ? pickFirstCategory(data) : null);
          }
          if (numericCategoryIdFromQuery) {
            const fromQuery = findCategoryInTree(data, numericCategoryIdFromQuery);
            if (fromQuery) return fromQuery;
          }
          return shouldAutoSelectCategory ? pickFirstCategory(data) : null;
        });
        sessionStorage.setItem(cacheKey, JSON.stringify(data));
      } catch (err) {
        console.error('Failed to load categories:', err);
        setCategories([]);
        setExpandedCategories(new Set());
        setSelectedCategory(null);
        setSelectedCategoryDetails(null);
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, [resolvedCourseId, apiBaseUrl, numericCategoryIdFromQuery]);

  useEffect(() => {
    const fetchCategoryDetails = async () => {
      if (!selectedCategory) {
        setSelectedCategoryDetails(null);
        return;
      }

      try {
        const response = await axios.get<Category>(`${apiBaseUrl}/categories/${selectedCategory.id}/details`, {
          headers: {
            ...getOrganizationHeaders(),
          },
        });
        setSelectedCategoryDetails(response.data);
      } catch (err) {
        console.error('Failed to load category details:', err);
        setSelectedCategoryDetails(selectedCategory);
      }
    };

    fetchCategoryDetails();
  }, [selectedCategory, apiBaseUrl]);

  useEffect(() => {
    const fetchEnrollment = async () => {
      if (!isAuthenticated || !resolvedCourseId) {
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const response = await axios.get<UserEnrollment[]>(`${apiBaseUrl}/courses/user/enrollments`, {
          headers: {
            Authorization: `Bearer ${token}`,
            ...getOrganizationHeaders(),
          },
        });

        const currentEnrollment = response.data.find((item) => item.courseId === resolvedCourseId) ?? null;
        setEnrollment(currentEnrollment);
      } catch (err) {
        console.error('Failed to load enrollment details:', err);
      }
    };

    fetchEnrollment();
  }, [isAuthenticated, getAccessTokenSilently, resolvedCourseId, apiBaseUrl]);

  const hasPaidSubscription = useMemo(() => {
    return !!enrollment && enrollment.subscriptionTier > 1;
  }, [enrollment]);

  const categoryNavigationList = useMemo(() => {
    const flatten = (items: Category[]): Category[] => {
      const sortedItems = sortCategoriesByLevelAndOrder(items);
      const flattened: Category[] = [];

      sortedItems.forEach((item) => {
        flattened.push(item);
        if (item.subCategories && item.subCategories.length > 0) {
          flattened.push(...flatten(item.subCategories));
        }
      });

      return flattened;
    };

    return flatten(categories);
  }, [categories]);

  const hasNextCategory = useMemo(() => {
    if (!selectedCategory) {
      return false;
    }

    const currentIndex = categoryNavigationList.findIndex((item) => item.id === selectedCategory.id);
    return currentIndex >= 0 && currentIndex < categoryNavigationList.length - 1;
  }, [selectedCategory, categoryNavigationList]);

  const findCategoryPathIds = (items: Category[], categoryId: number, parentPath: number[] = []): number[] | null => {
    for (const item of items) {
      const currentPath = [...parentPath, item.id];

      if (item.id === categoryId) {
        return currentPath;
      }

      if (item.subCategories && item.subCategories.length > 0) {
        const foundPath = findCategoryPathIds(item.subCategories, categoryId, currentPath);
        if (foundPath) {
          return foundPath;
        }
      }
    }

    return null;
  };

  const handleNextCategory = () => {
    if (!selectedCategory) {
      return;
    }

    const currentIndex = categoryNavigationList.findIndex((item) => item.id === selectedCategory.id);
    if (currentIndex < 0 || currentIndex >= categoryNavigationList.length - 1) {
      return;
    }

    const nextCategory = categoryNavigationList[currentIndex + 1];
    setSelectedCategory(nextCategory);
    setShowCategoriesMenu(false);

    const nextCategoryPath = findCategoryPathIds(categories, nextCategory.id) ?? [];
    if (nextCategoryPath.length > 0) {
      setExpandedCategories((previous) => {
        const nextExpanded = new Set(previous);
        nextCategoryPath.forEach((pathId) => nextExpanded.add(pathId));
        return nextExpanded;
      });
    }
  };

  const handleBackToSyllabusOrCourses = () => {
    if (selectedCategory) {
      setSelectedCategory(null);
      setShowCategoriesMenu(true);

      requestAnimationFrame(() => {
        if (typeof window !== 'undefined' && window.innerWidth < 1024) {
          syllabusToggleButtonRef.current?.focus();
          syllabusSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          syllabusSectionRef.current?.focus();
        }
      });

      return;
    }

    navigate(slugAwarePath('/courses'));
  };

  const getPracticeTasksUrl = () => {
    const courseRef = course?.inforPagenav ?? resolvedCourseId?.toString();
    if (!courseRef) {
      return slugAwarePath('/practice');
    }
    const params = new URLSearchParams({
      courseId: courseRef,
    });

    if (selectedCategory?.id) {
      params.set('categoryId', selectedCategory.id.toString());
    }

    return slugAwarePath(`/practice?${params.toString()}`);
  };

  // Recursively filter categories and subcategories by search text
  const filterCategories = (items: Category[], search: string): Category[] => {
    if (!search.trim()) return items;
    const lower = search.trim().toLowerCase();
    return items
      .map((cat) => {
        const sub = cat.subCategories ? filterCategories(cat.subCategories, search) : [];
        if (
          cat.name.toLowerCase().includes(lower) ||
          (sub && sub.length > 0)
        ) {
          return { ...cat, subCategories: sub };
        }
        return null;
      })
      .filter(Boolean) as Category[];
  };

  const renderCategoryTree = (items: Category[], level = 0): React.ReactNode => {
    const sortedItems = sortCategoriesByLevelAndOrder(items);
    return sortedItems.map((category) => (
      <React.Fragment key={category.id}>
        <div className="mb-1" style={{ paddingLeft: `${level * 14}px` }}>
          <div className="flex items-center gap-1.5">
            {level > 0 ? (
              <div className="relative h-8 w-4 shrink-0">
                <span className="absolute left-1 top-0 bottom-0 border-l border-gray-200" />
                <span className="absolute left-1 top-1/2 w-3 border-t border-gray-200" />
              </div>
            ) : (
              <div className="h-8 w-1 shrink-0" />
            )}

            {(() => {
              const childCount = category.subCategories?.length ?? 0;
              const hasSubCategories = childCount > 0;
              const isSelected = selectedCategory?.id === category.id;

              return (
            <button
              onClick={() => {
                if (hasSubCategories) {
                  setExpandedCategories((previous) => {
                    const next = new Set(previous);
                    if (previous.has(category.id)) {
                      next.delete(category.id);
                    } else {
                      next.add(category.id);
                    }
                    return next;
                  });
                  // Do NOT show details for parent categories
                  return;
                }
                // Only show details for leaf categories
                setSelectedCategory(category);
                setSelectedCategoryDetails(null);
                if (typeof window !== 'undefined' && window.innerWidth < 1024) {
                  setShowCategoriesMenu(false);
                }
              }}
              className={`flex-1 min-w-0 rounded-md px-2 py-2 text-left transition-all ${
                isSelected
                  ? 'bg-indigo-100 text-indigo-900 border border-indigo-300 border-l-4 border-l-indigo-600 shadow-md'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              }`}
              aria-label="Select category"
            >
              <div className="flex items-center justify-between gap-2">
                <span className={`flex-1 block truncate ${isSelected ? 'font-bold' : ''} ${level === 0 ? 'font-semibold text-sm sm:text-base' : 'font-medium text-sm'}`}>
                  {getTreeCategoryName(category)}
                </span>
                {/* Show topic count for subcategories that have subcategories (not topmost parent), right aligned */}
                {level > 0 && hasSubCategories && (
                  <span className="ml-2 px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold border border-gray-200 self-center ml-auto">
                    {childCount} topics
                  </span>
                )}
                {/* Show chevron only for categories with children */}
                {hasSubCategories && (
                  <span
                    className="shrink-0 text-xs font-bold text-gray-400 ml-2"
                    style={{
                      transition: 'transform 0.2s ease',
                      transform: expandedCategories.has(category.id) ? 'rotate(90deg)' : 'rotate(0deg)',
                      display: 'inline-flex',
                      alignItems: 'center'
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 20 20" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7 6l5 4-5 4" />
                    </svg>
                  </span>
                )}
              </div>
            </button>
              );
            })()}
          </div>

          {category.subCategories && category.subCategories.length > 0 && expandedCategories.has(category.id) && (
            <div className="mt-1">
              {renderCategoryTree(category.subCategories, level + 1)}
            </div>
          )}
        </div>
      </React.Fragment>
    ));
  };

  const convertLatexToHtml = (text: string): string => {
    if (!text) return text;

    let converted = text;
    converted = converted.replace(/\\textbf\{([^}]*)\}/g, '<strong>$1</strong>');
    converted = converted.replace(/\\textit\{([^}]*)\}/g, '<em>$1</em>');
    converted = converted.replace(/\\underline\{([^}]*)\}/g, '<u>$1</u>');
    converted = converted.replace(/\\texttt\{([^}]*)\}/g, '<code>$1</code>');
    converted = converted.replace(/\\text\{([^}]*)\}/g, '$1');

    return converted;
  };

  const renderDiagramsByPlacement = (selectedCat: Category | null, placementType: number): React.ReactNode => {
    if (!selectedCat?.categoryDiagrams || selectedCat.categoryDiagrams.length === 0) {
      return null;
    }

    const diagramsForPlacement = selectedCat.categoryDiagrams
      .filter((diagram) => diagram.placementType === placementType)
      .sort((a, b) => a.displayOrder - b.displayOrder);

    if (diagramsForPlacement.length === 0) {
      return null;
    }

    return (
      <div className="space-y-3">
        {diagramsForPlacement.map((diagram) => {
          const introHtmlValue = diagram.introHtml?.trim();
          const captionOverrideValue = diagram.captionOverride?.trim();
          const defaultCaptionValue = diagram.defaultCaption?.trim();
          const imageUrlValue = diagram.imageUrl?.trim();
          const diagramSource = imageUrlValue
            ? resolveDiagramImageUrl(imageUrlValue)
            : `${config.apiBaseUrl}/api/diagrams/${diagram.diagramId}/svg`;

          return (
            <div key={diagram.id} className="border border-gray-200 rounded-lg bg-white p-3">
              {introHtmlValue && (
                <div className="mb-3">
                  <div className="text-xs font-bold text-gray-700 mb-1">Intro</div>
                  <div
                    className="html-content text-sm text-gray-700 leading-relaxed text-left"
                    dangerouslySetInnerHTML={{ __html: introHtmlValue }}
                  />
                </div>
              )}

              <div className="border border-gray-300 rounded-md bg-white p-2 flex justify-center">
                <img
                  src={diagramSource}
                  alt={`${diagram.diagramType} diagram`}
                  className="h-auto block mx-auto"
                  style={{ width: getDiagramWidthBySize(diagram.displaySize), maxWidth: '100%' }}
                />
              </div>

              {captionOverrideValue && (
                <div className="mt-2 text-center">
                  <div className="text-xs font-bold text-gray-700 mb-1">Caption Override</div>
                  <p className="m-0 text-xs text-gray-600">{captionOverrideValue}</p>
                </div>
              )}

              {!captionOverrideValue && defaultCaptionValue && (
                <p className="mt-2 mb-0 text-xs text-gray-600 text-center">{defaultCaptionValue}</p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const handleDynamicComponentSelection = (componentId: number, value: number) => {
    setDynamicComponentSelections((previous) => ({
      ...previous,
      [componentId]: value,
    }));
  };

  const renderDynamicComponentsByPlacement = (selectedCat: Category | null, placementType: number): React.ReactNode => {
    return (
      <DynamicComponentRenderer
        components={selectedCat?.dynamicComponents}
        placementType={placementType}
        selections={dynamicComponentSelections}
        onSelectionChange={handleDynamicComponentSelection}
      />
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div
      className="bg-gradient-to-br from-app-page-from to-app-page-to"
      style={{
        width: '100%',
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 md:px-6 py-3 md:py-4 text-white shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm md:text-base font-semibold">
              <button
                onClick={() => navigate(slugAwarePath('/courses'))}
                className="text-blue-100 hover:text-white transition-colors"
              >
                All courses
              </button>
              <span className="text-blue-200">›</span>
              <span className="text-white truncate max-w-[210px] sm:max-w-none">
                {course?.name ?? 'Course Details'}
              </span>
              <span className="text-blue-200">›</span>
              <span className="text-blue-100">Syllabus</span>
            </div>
          </div>
          <div className="hidden md:flex lg:hidden flex-wrap items-stretch gap-2 self-stretch">
            {isAuthenticated && enrollment && !hasPaidSubscription && (
              <button
                onClick={() => navigate(slugAwarePath(`/billing?courseId=${course?.id ?? numericCourseId}`))}
                className="inline-flex min-h-[58px] items-center gap-3 rounded-2xl bg-gradient-to-r from-yellow-500 to-orange-500 px-4 py-3 text-white shadow-sm transition-colors hover:from-yellow-600 hover:to-orange-600"
              >
                <span className="text-lg leading-none">🔓</span>
                <span className="flex flex-col items-start leading-tight">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-yellow-100">Access</span>
                  <span className="text-sm font-semibold">Upgrade</span>
                </span>
              </button>
            )}
            <button
              onClick={() => navigate(getPracticeTasksUrl())}
              className="inline-flex min-h-[58px] items-center gap-3 rounded-2xl bg-green-500 px-4 py-3 text-white shadow-sm transition-colors hover:bg-green-600"
            >
              <span className="text-lg leading-none">📖</span>
              <span className="flex flex-col items-start leading-tight">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-green-100">Practice</span>
                <span className="text-sm font-semibold">Questions</span>
              </span>
            </button>
            <button
              onClick={() => navigate(slugAwarePath(`/practice-tests/${course?.id ?? numericCourseId}`))}
              className="inline-flex min-h-[58px] items-center gap-3 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-blue-700 shadow-sm transition-colors hover:bg-blue-100"
            >
              <span className="text-lg leading-none">≡</span>
              <span className="flex flex-col items-start leading-tight">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-blue-500">Testing</span>
                <span className="text-sm font-semibold">Exams</span>
              </span>
            </button>
            <button
              onClick={handleBackToSyllabusOrCourses}
              className="inline-flex min-h-[58px] items-center gap-3 rounded-2xl border border-white/25 bg-white/10 px-4 py-3 text-white shadow-sm transition-colors hover:bg-white/20"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 20 20" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 6l-5 4 5 4" />
              </svg>
              <span className="flex flex-col items-start leading-tight">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-blue-100">Navigate</span>
                <span className="text-sm font-semibold">Back</span>
              </span>
            </button>
            <button
              onClick={handleNextCategory}
              disabled={!hasNextCategory}
              className="inline-flex min-h-[58px] items-center gap-3 rounded-2xl bg-gradient-to-b from-blue-400 to-blue-600 px-4 py-3 text-white shadow-sm transition-all hover:from-blue-500 hover:to-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="flex flex-col items-start leading-tight">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-blue-100">Navigate</span>
                <span className="text-sm font-semibold">Next</span>
              </span>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 20 20" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 6l5 4-5 4" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-3 pb-40 md:p-4 md:pb-4 lg:p-6 lg:pb-6">

        {error && (
          <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded-lg mb-3 md:mb-4">
            {error}
          </div>
        )}

        {course && (
          <>
            <button
              ref={syllabusToggleButtonRef}
              onClick={() => setShowCategoriesMenu((prev) => !prev)}
              className="hidden md:flex lg:hidden w-full mb-3 px-4 py-3 bg-white border border-gray-200 rounded-lg text-left font-semibold text-gray-800 items-center justify-between"
            >
              <span className="flex items-center gap-2">
                <span className="text-lg">☰</span>
                <span>Syllabus</span>
              </span>
              <span className="text-sm text-gray-600">{showCategoriesMenu ? '▲' : '▼'}</span>
            </button>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
            <div
              ref={syllabusSectionRef}
              tabIndex={-1}
              className={`${showCategoriesMenu ? 'block' : 'hidden'} lg:block lg:col-span-4 space-y-4 sm:space-y-6`}
            >
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="md:min-h-[620px]">
                  {/* Category Search Box */}
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-t-md" style={{ marginBottom: 8 }}>
                    <div className="relative w-full" style={{ width: '100%' }}>
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                        {/* Heroicons solid magnifying glass */}
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
                        </svg>
                      </span>
                      <input
                        type="text"
                        value={categorySearch}
                        onChange={e => setCategorySearch(e.target.value)}
                        placeholder="Search topics..."
                        className="w-full rounded border border-gray-300 pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                        style={{ width: '100%', minWidth: 0, boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                  {categoriesLoading && categories.length === 0 ? (
                    <div className="p-4 text-sm text-gray-600 md:h-[620px] flex items-start">
                      <span className="inline-flex items-center gap-2 text-sm text-blue-700 font-medium">
                        <span className="inline-block h-4 w-4 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
                        Loading syllabus...
                      </span>
                    </div>
                  ) : categories && categories.length > 0 ? (
                    <div className="md:max-h-[680px] overflow-y-auto md:min-h-[620px] p-2">
                      {renderCategoryTree(filterCategories(categories, categorySearch))}
                    </div>
                  ) : (
                    <div className="p-4 text-sm text-gray-600 md:h-[620px]">No syllabus items are configured for this course.</div>
                  )}
                </div>
              </div>

            </div>

            <div className={`lg:col-span-8 ${!selectedCategory ? 'hidden lg:block' : 'block'}`}>
              <div className="bg-white rounded-lg shadow-lg overflow-hidden h-full">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 sm:p-5 text-white text-left">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center text-lg shrink-0">📖</div>
                      <div className="min-w-0">
                        <h2 className="text-lg sm:text-xl font-bold text-left leading-snug">{getDisplayCategoryName(selectedCategoryDetails ?? selectedCategory)}</h2>
                        <p className="text-blue-100 text-sm mt-0.5 text-left">Overview, details, and tips</p>
                      </div>
                    </div>
                    <div className="hidden lg:flex items-start justify-end gap-2 shrink-0">
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {isAuthenticated && enrollment && !hasPaidSubscription && (
                          <button
                            onClick={() => navigate(slugAwarePath(`/billing?courseId=${course?.id ?? numericCourseId}`))}
                            className="shrink-0 whitespace-nowrap px-3 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-xl text-sm font-semibold shadow-sm hover:from-yellow-600 hover:to-orange-600 transition-colors"
                          >
                            Upgrade
                          </button>
                        )}
                        <button
                          onClick={() => navigate(getPracticeTasksUrl())}
                          className="shrink-0 whitespace-nowrap inline-flex items-center gap-2 rounded-xl bg-green-500/70 px-3 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-green-500/85"
                        >
                          <span className="text-sm leading-none">📖</span>
                          Questions
                        </button>
                        <button
                          onClick={() => navigate(slugAwarePath(`/practice-tests/${course?.id ?? numericCourseId}`))}
                          className="shrink-0 whitespace-nowrap inline-flex items-center gap-2 rounded-xl border border-blue-100 bg-blue-50/70 px-3 py-2 text-sm font-semibold text-blue-700 shadow-sm transition-colors hover:bg-blue-50/85"
                        >
                          <span className="text-sm leading-none">≡</span>
                          Exams
                        </button>
                        <button
                          onClick={handleBackToSyllabusOrCourses}
                          aria-label="Back"
                          title="Back"
                          className="shrink-0 inline-flex items-center justify-center rounded-xl border border-white/25 bg-white/10 px-3 py-2 text-white shadow-sm transition-colors hover:bg-white/20"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 20 20" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13 6l-5 4 5 4" />
                          </svg>
                        </button>
                        <button
                          onClick={handleNextCategory}
                          disabled={!hasNextCategory}
                          aria-label="Next"
                          title="Next"
                          className="shrink-0 inline-flex items-center justify-center rounded-xl bg-gradient-to-b from-blue-400 to-blue-600 px-3 py-2 text-white shadow-sm transition-all hover:from-blue-500 hover:to-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 20 20" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M7 6l5 4-5 4" />
                          </svg>
                        </button>
                      </div>
                      {selectedCategory && categoryNavigationList.length > 0 && (
                        <span className="shrink-0 text-xs font-semibold bg-white/20 rounded-full px-2.5 py-1 whitespace-nowrap">
                          {categoryNavigationList.findIndex(c => c.id === selectedCategory.id) + 1} / {categoryNavigationList.length}
                        </span>
                      )}
                    </div>
                    <div className="lg:hidden">
                      {selectedCategory && categoryNavigationList.length > 0 && (
                        <span className="shrink-0 text-xs font-semibold bg-white/20 rounded-full px-2.5 py-1 whitespace-nowrap">
                          {categoryNavigationList.findIndex(c => c.id === selectedCategory.id) + 1} / {categoryNavigationList.length}
                        </span>
                      )}
                    </div>
                  </div>
                  {selectedCategory && categoryNavigationList.length > 0 && (
                    <div className="mt-3">
                      <div className="h-1.5 rounded-full bg-white/20 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-white/70 transition-all duration-300"
                          style={{ width: `${((categoryNavigationList.findIndex(c => c.id === selectedCategory.id) + 1) / categoryNavigationList.length) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="p-4 sm:p-6 space-y-6 text-left" style={{ textAlign: 'left' }}>
                  {selectedCategory ? (
                    <>
                      {(() => {
                        const selectedCategoryData = selectedCategoryDetails ?? selectedCategory;
                        const curriculumMetadataItems = getCurriculumMetadataItems(selectedCategoryData);

                        return curriculumMetadataItems.length > 0 ? (
                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 sm:p-4">
                            <h3 className="text-base font-semibold text-slate-900 mb-3">Curriculum Metadata</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {curriculumMetadataItems.map((item) => (
                                <div key={item.label} className={`rounded-md border border-slate-200 bg-white p-3 ${item.fullWidth ? 'md:col-span-2' : ''}`}>
                                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">{item.label}</div>
                                  <div className="text-sm text-slate-800 leading-relaxed">{item.value}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null;
                      })()}

                      {(selectedCategoryDetails ?? selectedCategory).description?.trim() && (
                        <div className="border-l-4 border-blue-400 pl-4 w-full text-left" style={{ textAlign: 'left' }}>
                          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-blue-400 inline-block shrink-0" />
                            Overview
                          </h3>
                          <div
                            className="html-content course-overview-content text-sm sm:text-base text-gray-700 leading-relaxed w-full"
                            dangerouslySetInnerHTML={{ __html: (selectedCategoryDetails ?? selectedCategory).description }}
                            style={{ textAlign: 'left' }}
                          />
                        </div>
                      )}

                      {renderDiagramsByPlacement(selectedCategoryDetails ?? selectedCategory, 1)}
                      {renderDynamicComponentsByPlacement(selectedCategoryDetails ?? selectedCategory, 1)}

                      {(selectedCategoryDetails ?? selectedCategory).detailedDescription?.trim() && (
                        <div className="border-l-4 pl-4">
                          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-indigo-400 inline-block shrink-0" />
                            Details
                          </h3>
                          <div
                            className="html-content text-sm sm:text-base text-gray-700 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: (selectedCategoryDetails ?? selectedCategory).detailedDescription ?? '' }}
                            style={{ textAlign: 'left' }}
                          />
                        </div>
                      )}

                      {renderDiagramsByPlacement(selectedCategoryDetails ?? selectedCategory, 2)}
                      {renderDynamicComponentsByPlacement(selectedCategoryDetails ?? selectedCategory, 2)}
                      {renderDiagramsByPlacement(selectedCategoryDetails ?? selectedCategory, 3)}
                      {renderDynamicComponentsByPlacement(selectedCategoryDetails ?? selectedCategory, 3)}

                      {(selectedCategoryDetails ?? selectedCategory).example?.trim() && (
                        <div className="p-3 sm:p-4 rounded-lg border border-green-200 bg-green-50">
                          <h3 className="text-base font-semibold text-green-900 mb-2 flex items-center gap-2">💡 Examples</h3>
                          <div
                            className="html-content text-sm sm:text-base text-green-900 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: convertLatexToHtml((selectedCategoryDetails ?? selectedCategory).example ?? '') }}
                            style={{ textAlign: 'left' }}
                          />
                        </div>
                      )}

                      {renderDiagramsByPlacement(selectedCategoryDetails ?? selectedCategory, 4)}
                      {renderDynamicComponentsByPlacement(selectedCategoryDetails ?? selectedCategory, 4)}
                      {renderDiagramsByPlacement(selectedCategoryDetails ?? selectedCategory, 5)}
                      {renderDynamicComponentsByPlacement(selectedCategoryDetails ?? selectedCategory, 5)}

                      {(selectedCategoryDetails ?? selectedCategory).tips && (
                        <div className="p-3 sm:p-4 rounded-lg border border-amber-200 bg-amber-50">
                          <h3 className="text-base font-semibold text-amber-900 mb-2 flex items-center gap-2">⭐ Tips</h3>
                          <div
                            className="html-content text-sm sm:text-base text-amber-900 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: (selectedCategoryDetails ?? selectedCategory).tips ?? '' }}
                            style={{ textAlign: 'left' }}
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <div className="text-5xl mb-4">📚</div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-1">Pick a topic to begin</h3>
                      <p className="text-sm text-gray-500 max-w-xs">Select a category from the syllabus on the left to view its overview, details, and tips.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          </>
        )}

      </div>

      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-2 py-4 min-h-[72px]">
        <div className="flex flex-nowrap items-center justify-end gap-2 overflow-x-auto rounded-2xl border border-slate-200 bg-white/95 p-2 shadow-sm">
          {isAuthenticated && enrollment && !hasPaidSubscription && (
            <button
              onClick={() => navigate(slugAwarePath(`/billing?courseId=${course?.id ?? numericCourseId}`))}
              className="shrink-0 whitespace-nowrap px-3 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg text-sm font-semibold hover:from-yellow-600 hover:to-orange-600 transition-colors"
            >
              Upgrade
            </button>
          )}
          <button
            onClick={() => navigate(getPracticeTasksUrl())}
            className="shrink-0 whitespace-nowrap inline-flex items-center gap-2 rounded-xl bg-green-500 px-3 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-green-600"
          >
            <span className="text-sm leading-none">📖</span>
            Questions
          </button>
          <button
            onClick={() => navigate(slugAwarePath(`/practice-tests/${course?.id ?? numericCourseId}`))}
            className="shrink-0 whitespace-nowrap inline-flex items-center gap-2 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 shadow-sm transition-colors hover:bg-blue-100"
          >
            <span className="text-sm leading-none">≡</span>
            Exams
          </button>
          <button
            onClick={handleBackToSyllabusOrCourses}
            aria-label="Back"
            title="Back"
            className="shrink-0 inline-flex items-center justify-center rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-slate-600 shadow-sm transition-colors hover:bg-slate-100"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 20 20" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 6l-5 4 5 4" />
            </svg>
          </button>
          <button
            onClick={handleNextCategory}
            disabled={!hasNextCategory}
            aria-label="Next"
            title="Next"
            className="shrink-0 inline-flex items-center justify-center rounded-xl bg-gradient-to-b from-blue-400 to-blue-600 px-3 py-2 text-white shadow-sm transition-all hover:from-blue-500 hover:to-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 20 20" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 6l5 4-5 4" />
            </svg>
          </button>
        </div>
      </div>

    </div>
  );
};

export default CourseDetails;
